import { Show, createMemo, type Component, type JSX } from 'solid-js';
import { Text } from '~ui/text';
import { NA, exchangeCode, formatCurrency, formatDay, formatPercent, roundLargeNumber, todayISO } from '~/lib/format';
import {
    annualDistribution,
    assets,
    candles,
    distributionFrequency,
    distributions,
    effectiveManagementFee,
    fund,
    instrumentYield,
    issuerOf,
    managementFee,
    mandateOf,
    passesThroughFees,
    price,
    returnOfCapitalShare,
    riskRating,
} from '~data';

// Everything the artefact knows about one fund, as a column of rows.
//
// One implementation, used by the Explore side panel and by the Fund page. They
// showed slightly different subsets before, which is how two views of the same
// fund end up disagreeing about what is known — the fix is not to keep them in
// step but to make it impossible for them to differ.

const Fact: Component<{ label: string; children: JSX.Element; hint?: string }> = (props) => (
    <div class='flex h-row items-center justify-between gap-2 border-b px-2 whitespace-nowrap' title={props.hint}>
        <Text tone='muted'>{props.label}</Text>
        {props.children}
    </div>
);

export const FundFacts: Component<{ symbol: string }> = (props) => {
    const profile = () => fund(props.symbol);
    const asOf = todayISO();

    // Change over the whole series on file — the same window the chart draws, so
    // the number and the picture cannot disagree.
    const change = createMemo(() => {
        const series = candles(props.symbol);
        if (series.length < 2 || series[0].close <= 0) return null;
        return ((series[series.length - 1].close - series[0].close) / series[0].close) * 100;
    });

    /** The most recent payment per unit — what one unit pays you next time, as
        opposed to what it paid over the last year. */
    const perPayment = createMemo(() => distributions(props.symbol).at(-1)?.amount ?? null);

    /**
     * The direct fee and what holding it actually costs.
     *
     * A fund of funds charges little or nothing directly and passes on the fees
     * of what it holds, so its published figure is not comparable to a fund
     * running its own book. Both are shown wherever they differ — the published
     * one because it is what the issuer says, the effective one because it is
     * what you pay.
     */
    const direct = createMemo(() => managementFee(props.symbol));
    const effective = createMemo(() => effectiveManagementFee(props.symbol));
    const feesDiffer = createMemo(() => effective() != null && direct() != null && Math.abs(effective()! - direct()!) > 0.005);

    return (
        <>
            <Fact label='Price'>
                <Text weight='medium'>{formatCurrency(price(props.symbol))}</Text>
            </Fact>
            <Fact label='Change on file' hint='Price alone, first close on file to last — distributions not counted'>
                <Text tone={change() == null ? undefined : change()! >= 0 ? 'green' : 'red'}>{formatPercent(change(), 1)}</Text>
            </Fact>
            <Fact label='Yield'>{formatPercent(instrumentYield(props.symbol, asOf))}</Fact>
            <Fact label='Distribution / yr'>
                <Text tone='green'>{formatCurrency(annualDistribution(props.symbol, asOf))}</Text>
            </Fact>
            <Fact label='Per payment' hint='The most recent distribution, per unit'>
                <Text tone='green'>{formatCurrency(perPayment(), 4)}</Text>
            </Fact>
            <Fact label='Return of capital' hint='Share of the last published tax year that was return of capital'>
                {formatPercent(returnOfCapitalShare(props.symbol), 0)}
            </Fact>
            <Fact label='Management fee'>{formatPercent(direct())}</Fact>
            {/* Only where it says something the row above does not. On a fund
                running its own book the two are the same number. */}
            <Show when={feesDiffer()}>
                <Fact label='Effective fee' hint={passesThroughFees(props.symbol) ? 'Includes the fees of the funds this one holds' : undefined}>
                    <Text weight='medium'>{formatPercent(effective())}</Text>
                </Fact>
            </Show>
            <Fact label='Frequency'>
                <Text class='truncate'>{distributionFrequency(props.symbol) ?? NA}</Text>
            </Fact>
            <Fact label='Risk'>
                <Text class='truncate'>{riskRating(props.symbol) ?? NA}</Text>
            </Fact>
            <Fact label='Mandate'>
                <Text class='truncate'>{mandateOf(props.symbol) ?? NA}</Text>
            </Fact>
            <Fact label='Issuer'>
                <Text class='truncate'>{issuerOf(props.symbol) ?? NA}</Text>
            </Fact>
            {/* The code, not the name — see exchangeCode. Full name on hover,
                since the code is the abbreviation and not everyone reads them. */}
            <Fact label='Exchange' hint={profile()?.exchange ?? undefined}>
                <Text class='truncate'>{exchangeCode(profile()?.exchange) ?? NA}</Text>
            </Fact>
            <Fact label='Currency'>
                <Text class='truncate'>{profile()?.currency ?? NA}</Text>
            </Fact>
            {/* Whether the fund hedges its foreign currency back to Canadian
                dollars, which decides whether a US holding's return reaches you
                as the fund's return or as that plus the exchange rate. */}
            <Fact label='Hedging' hint={profile()?.hedging ?? undefined}>
                <Text class='truncate'>{profile()?.hedging ?? NA}</Text>
            </Fact>
            <Fact label='Assets'>{assets(props.symbol) == null ? NA : `$${roundLargeNumber(assets(props.symbol))}`}</Fact>
            <Fact label='Inception'>
                <Text tone='muted'>{profile()?.inceptionDate ? formatDay(profile()!.inceptionDate!) : NA}</Text>
            </Fact>
        </>
    );
};

export default FundFacts;
